import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Plus,
  ArrowLeft,
  Building,
  AlertCircle,
  Sparkles,
  ChevronRight,
  Briefcase,
  RotateCcw,
  ExternalLink,
} from "lucide-react";
import { getClient, listBusinesses, createBot } from "../../../api/chatbotApi";
import useAuth from "../../../hooks/useAuth";
import BotCard, { filterBots } from "../../../components/admin/BotCard";
import { isTenantAdmin } from "../../../util/accessControl";

const initialBizForm = {
  name: "",
  slug: "",
  industry: "",
  business_type: "",
  timezone: "Asia/Kolkata",
  plan: "starter",
  system_prompt: "",
};

export default function ChatbotClientDetailPage({ dashboardClientId }) {
  const { clientId: routeClientId } = useParams();
  const clientId = dashboardClientId || routeClientId;
  const { user } = useAuth();
  const directoryPath = isTenantAdmin(user) ? "/dashboard" : "/clients";
  const directoryLabel = isTenantAdmin(user) ? "Dashboard" : "Clients";
  const navigate = useNavigate();
  const location = useLocation();
  const isNewBotPage = location.pathname.endsWith("/new-bot");
  const [client, setClient] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Business modal state
  const [showBizModal, setShowBizModal] = useState(false);
  const [bizForm, setBizForm] = useState(initialBizForm);
  const [bizTouched, setBizTouched] = useState({});
  const [bizModalError, setBizModalError] = useState("");
  const [bizSaving, setBizSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [c, b] = await Promise.all([getClient(clientId), listBusinesses(clientId)]);
      setClient(c);
      setBusinesses(b);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load client details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [clientId, isNewBotPage]);

  const handleNameChangeForBiz = (val) => {
    const slug = val
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setBizForm((prev) => ({
      ...prev,
      name: val,
      slug:
        prev.slug === "" || prev.slug === prev.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")
          ? slug
          : prev.slug,
      system_prompt:
        prev.system_prompt === "" || prev.system_prompt.startsWith("You are the official AI assistant for")
          ? `You are the official AI assistant for ${val || "our business"}.`
          : prev.system_prompt,
    }));
  };

  const touchBizField = (field) => setBizTouched((prev) => ({ ...prev, [field]: true }));

  const validateBizField = (field, currentForm) => {
    const val = (currentForm[field] || "").trim();
    if (field === "name") {
      if (!val) return "Client name is required.";
      if (!/^[a-zA-Z0-9\s]+$/.test(val)) return "Name must only contain letters, numbers, and spaces.";
      if (val.length < 3) return "Name must be at least 3 characters.";
    }
    if (field === "slug") {
      if (!val) return "Slug is required.";
      if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(val)) return "Slug must be lowercase letters, numbers, and hyphens only.";
    }
    if (field === "industry" && !val) return "Industry is required.";
    if (field === "business_type" && !val) return "Business type is required.";
    return "";
  };

  const bizErrors = {};
  for (const field of ["name", "slug", "industry", "business_type"]) {
    bizErrors[field] = bizTouched[field] ? validateBizField(field, bizForm) : "";
  }

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    setBizModalError("");

    const trimmedName = bizForm.name.trim();
    const trimmedSlug = bizForm.slug.trim();
    const trimmedIndustry = bizForm.industry.trim();
    const trimmedBusinessType = bizForm.business_type.trim();

    const nameError = validateBizField("name", bizForm);
    if (nameError) {
      setBizModalError(nameError);
      return;
    }

    if (!trimmedSlug || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(trimmedSlug)) {
      setBizModalError("Slug must be lowercase letters, numbers, and hyphens only.");
      return;
    }

    if (!trimmedIndustry || !trimmedBusinessType) {
      setBizModalError("Industry and Business type are required.");
      return;
    }

    setBizSaving(true);
    try {
      const payload = {
        name: trimmedName,
        slug: trimmedSlug,
        domain: trimmedSlug,
        industry: trimmedIndustry,
        business_type: trimmedBusinessType,
        timezone: bizForm.timezone || "Asia/Kolkata",
        plan: bizForm.plan || "starter",
        system_prompt: bizForm.system_prompt.trim() || `You are the official AI assistant for ${trimmedName}.`,
      };

      await createBot(clientId, payload);
      setBizForm(initialBizForm);
      setBizTouched({});
      setBizModalError("");
      setShowBizModal(false);
      await load();
      if (isNewBotPage) {
        navigate(`/clients/${clientId}`);
      }
    } catch (err) {
      setBizModalError(err?.response?.data?.detail || "Error creating business / tenant");
    } finally {
      setBizSaving(false);
    }
  };

  const resetBizForm = () => {
    setBizForm(initialBizForm);
    setBizTouched({});
    setBizModalError("");
  };

  const filteredBusinesses = filterBots(businesses, searchTerm);

  const fieldInputClass = (fieldName) =>
    bizErrors[fieldName]
      ? "w-full rounded-xl border border-rose-300 bg-rose-50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-rose-400 focus:bg-white"
      : "w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#096dea] focus:bg-white";

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#096dea] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isNewBotPage && <>
      {/* Breadcrumb & Navigation */}
      {!dashboardClientId && <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to={directoryPath} className="hover:text-slate-900 transition flex items-center gap-1">
          <ArrowLeft size={16} /> {directoryLabel}
        </Link>
        <span>/</span>
        <span className="text-slate-900 font-semibold">{client?.name || "Client"}</span>
      </div>}

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle size={18} className="shrink-0 text-rose-600" />
          <span>{error}</span>
        </div>
      )}

      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <Briefcase size={20} className="text-[#096dea]" /> {client?.name || "Client"}'s Agent {filteredBusinesses.length}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              24X7 Support system of {client?.name}.
            </p>
          </div>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <input
              type="text"
              placeholder="Search businesses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm shadow-sm focus:border-[#096dea] focus:outline-none sm:w-64"
            />
            <button
              onClick={() => {
                resetBizForm();
                navigate(`/clients/${clientId}/new-bot`);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0959bb] focus:outline-none focus:ring-2 focus:ring-[#096dea]/20"
            >
              <Plus size={17} /> Add Bot
            </button>
          </div>
        </div>

        {filteredBusinesses.length === 0 ? (
          <div className="rounded-2xl border border-slate-200/80 bg-white p-12 text-center shadow-sm">
            <Building className="mx-auto h-12 w-12 text-slate-300" />
            <h3 className="mt-2 text-sm font-semibold text-slate-900">No businesses found</h3>
            <p className="mt-1 text-xs text-slate-500">
              {searchTerm ? "No matching businesses." : "Get started by registering a business under this client."}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  resetBizForm();
                  navigate(`/clients/${clientId}/new-bot`);
                }}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0959bb]"
              >
                <Plus size={16} /> Add Bot
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {filteredBusinesses.map((biz) => (
              <BotCard key={biz.id} bot={biz}
                onEdit={biz.id ? () => navigate(`/agents/${biz.id}/edit`) : undefined}>
                <div className="flex shrink-0 items-center gap-2">
                  {biz.id && <button type="button" onClick={() => navigate(`/agents/${encodeURIComponent(biz.id)}/customers`)}
                    className="rounded-lg bg-cyan-800 px-3 py-1.5 text-xs font-semibold text-white">
                    Workspace
                  </button>}
                  {biz.id && (
                    <button
                      onClick={() => navigate(`/agents/${biz.id}/meta-config`, {
                        state: {
                          from: dashboardClientId ? "/dashboard" : `/clients/${clientId}`,
                          fromLabel: client?.name || "Client",
                        },
                      })}
                      className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50/60 px-2.5 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                      title="View Meta Config"
                    >
                      Meta Config <ExternalLink size={12} />
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/agents/${biz.id}/agent-config`)}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-[#096dea] hover:text-[#096dea]"
                  >
                    Agent Config <ChevronRight size={14} />
                  </button>
                </div>
              </BotCard>
            ))}
          </div>
        )}
      </section>
      </>}

      {/* Add Bot Page */}
      {(showBizModal || isNewBotPage) && (
        <div className={isNewBotPage ? "mx-auto w-full max-w-4xl" : "fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-xs overflow-y-auto"}>
          <div className={isNewBotPage ? "w-full space-y-6" : "w-full max-w-3xl rounded-3xl bg-white p-6 md:p-8 shadow-2xl border border-slate-200 my-8 space-y-6 max-h-[92vh] overflow-y-auto"}>
            {isNewBotPage && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Link to={directoryPath} className="transition hover:text-slate-900">{directoryLabel}</Link>
                <span>/</span>
                <Link to={`/clients/${clientId}`} className="transition hover:text-slate-900">
                  {client?.name || "Client"}
                </Link>
                <span>/</span>
                <span className="font-semibold text-slate-900">New Bot</span>
              </div>
            )}

            {/* Form Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-slate-900">Create Business & Register Tenant</h2>
                  <span className="bg-blue-50 text-blue-700 border border-blue-100 text-[11px] font-bold px-2 py-0.5 rounded-md uppercase">
                    Unified Onboarding
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Creates a complete business tenant entity under client: <strong className="text-slate-700">{client?.name}</strong>
                </p>
              </div>
              {!isNewBotPage && (
                <button
                  type="button"
                  onClick={() => setShowBizModal(false)}
                  className="text-slate-400 hover:text-slate-600 rounded-xl p-1.5 hover:bg-slate-100 transition"
                >
                  ✕
                </button>
              )}
            </div>

            {bizModalError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex items-center gap-2.5">
                <AlertCircle size={18} className="shrink-0 text-rose-600" />
                <span>{bizModalError}</span>
              </div>
            )}

            <form onSubmit={handleCreateBusiness} className="space-y-6">
              {/* SECTION 1: General Information */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
                  <Briefcase size={15} className="text-[#096dea]" /> 1. General & Classification
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="new-bot-name" className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                      Client Name *
                    </label>
                    <input
                      id="new-bot-name"
                      type="text"
                      required
                      value={bizForm.name}
                      onFocus={() => touchBizField("name")}
                      onChange={(e) => handleNameChangeForBiz(e.target.value)}
                      placeholder="e.g. Acme Corp"
                      className={fieldInputClass("name")}
                      autoFocus
                    />
                    {bizErrors.name && <p className="text-xs text-rose-600 mt-1">{bizErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                      Slug *
                    </label>
                    <input
                      type="text"
                      required
                      value={bizForm.slug}
                      onFocus={() => touchBizField("slug")}
                      onChange={(e) => setBizForm((f) => ({ ...f, slug: e.target.value }))}
                      placeholder="e.g. acme-corp"
                      className={`${fieldInputClass("slug")} font-mono`}
                    />
                    {bizErrors.slug && <p className="text-xs text-rose-600 mt-1">{bizErrors.slug}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                      Industry *
                    </label>
                    <input
                      type="text"
                      required
                      value={bizForm.industry}
                      onFocus={() => touchBizField("industry")}
                      onChange={(e) => setBizForm((f) => ({ ...f, industry: e.target.value }))}
                      placeholder="e.g. Healthcare, IT, Logistics"
                      className={fieldInputClass("industry")}
                    />
                    {bizErrors.industry && <p className="text-xs text-rose-600 mt-1">{bizErrors.industry}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                      Business Type *
                    </label>
                    <input
                      type="text"
                      required
                      value={bizForm.business_type}
                      onFocus={() => touchBizField("business_type")}
                      onChange={(e) => setBizForm((f) => ({ ...f, business_type: e.target.value }))}
                      placeholder="e.g. Hospital, SaaS, Travel"
                      className={fieldInputClass("business_type")}
                    />
                    {bizErrors.business_type && (
                      <p className="text-xs text-rose-600 mt-1">{bizErrors.business_type}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                      Plan *
                    </label>
                    <select
                      value={bizForm.plan}
                      onChange={(e) => setBizForm((f) => ({ ...f, plan: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 outline-none transition focus:border-[#096dea] focus:bg-white"
                    >
                      <option value="starter">Starter</option>
                      <option value="growth">Growth</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                      Timezone
                    </label>
                    <input
                      type="text"
                      value={bizForm.timezone}
                      onChange={(e) => setBizForm((f) => ({ ...f, timezone: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:bg-white"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-2xs space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2">
                  <Sparkles size={15} className="text-[#096dea]" /> 3. AI Prompt
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                      AI System Prompt
                    </label>
                    <textarea
                      value={bizForm.system_prompt}
                      onChange={(e) => setBizForm((f) => ({ ...f, system_prompt: e.target.value }))}
                      rows={2}
                      placeholder="You are the official AI assistant for..."
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-xs focus:border-[#096dea] focus:bg-white focus:outline-none font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={resetBizForm}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
                >
                  <RotateCcw size={15} /> Reset Form
                </button>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => (isNewBotPage ? navigate(`/clients/${clientId}`) : setShowBizModal(false))}
                    className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={bizSaving}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#096dea] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#0959bb] disabled:opacity-50 transition shadow-sm"
                  >
                    {bizSaving ? "Registering..." : "Create Business & Tenant"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
